/* ==========================================================================
   FILE: services/chat.service.js
   IAMONEAI - Chat Service
   AI conversations
   ========================================================================== */

import { client } from '../api';

// ==========================================================================
// CHAT SERVICE
// ==========================================================================

const chatService = {
  /**
   * Send message to IAMONEAI
   * @param {string} text - User message
   * @param {string} iin - User's IIN (optional for anonymous)
   * @param {Object} options - Additional options
   * @param {string} options.context - Context mode (personal/work/family)
   * @returns {Promise<Object>} AI response
   */
  async send(text, iin = null, options = {}) {
    const body = { text };
    
    if (iin) body.iin = iin;
    if (options.context) body.context = options.context;

    return client.post('/ai/chat', body, {
      cache: false,
      retry: true,
      offline: 'queue', // Queue if offline
      priority: 'high',
    });
  },

  /**
   * Parse intent without executing
   * @param {string} text 
   * @param {string} iin 
   * @returns {Promise<Object>} Parsed intent
   */
  async parseIntent(text, iin = null) {
    const body = { text };
    if (iin) body.iin = iin;

    return client.post('/ai/parse', body, {
      cache: true,
      cacheTTL: 60000, // 1 minute
    });
  },

  /**
   * Switch context mode
   * @param {string} iin 
   * @param {string} context - personal/work/family
   * @returns {Promise<Object>}
   */
  async switchContext(iin, context) {
    return this.send(`switch to ${context} mode`, iin);
  },

  /**
   * Get suggested responses based on context
   * @param {string} iin 
   * @param {string} context 
   * @returns {Array<string>} Suggestions
   */
  getSuggestions(context = 'personal') {
    const suggestions = {
      personal: [
        "What's on my mind today?",
        "Show my goals",
        "How am I feeling?",
      ],
      work: [
        "What are my work priorities?",
        "Show my deadlines",
        "Help me draft an email",
      ],
      family: [
        "Any family events coming up?",
        "Show family goals",
        "Remind me about birthdays",
      ],
    };

    return suggestions[context] || suggestions.personal;
  },
};

// ==========================================================================
// EXPORTS
// ==========================================================================

export { chatService };
export default chatService;